import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import * as React from "react";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ServiceVersionsRepository from "../../repositories/ServiceVersionsRepository";
import { FilterCombobox, FilterComboboxOption } from "../common/FilterCombobox";
import { QUERY_KEYS } from "../../constants/QueryKeys";

interface ServiceVersionSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const ServiceVersionSelect: React.FC<ServiceVersionSelectProps> = ({
  value,
  onChange,
  disabled
}) => {
  const serviceVersionRepository = new ServiceVersionsRepository();
  const [version, setVersion] = useState(value);

  const handleClearClick = () => {
    setVersion("");
    onChange("");
  };

  const {
    data: serviceVersions = [],
    isLoading,
    isError,
  } = useQuery({ queryKey: [QUERY_KEYS.SERVICE_VERSIONS], queryFn:
    // A missing build cannot be installed: pinning it would stop the configuration's clients from updating.
    async () => (await serviceVersionRepository.getServiceVersions()).filter(z => !z.isHidden && !z.isBlobMissing), staleTime: 1000 * 60 * 10,
      refetchInterval: 1000 * 60,
      refetchOnMount: false,
      refetchOnWindowFocus: false, });

  const handleInputChange = (event: any) => {
    const { value } = event.target;
    event.target.value = value.replace(/[^0-9.]/g, '');
  };

  const handleSelectChange = (selectedValue: string) => {
    onChange(selectedValue);
    setVersion(selectedValue);
  };

  const versionOptions = useMemo<FilterComboboxOption[]>(() => {
    const opts: FilterComboboxOption[] = [];
    if (value && !(serviceVersions.findIndex((v) => v.version === value) > -1)) {
      opts.push({ value, label: value });
    }
    for (const v of serviceVersions) {
      opts.push({ value: v.version, label: v.version });
    }
    opts.push({ value: "custom", label: "Custom" });
    return opts;
  }, [value, serviceVersions]);

  return (
    <div>
      <Label id="service-version-label">
        Service Version
      </Label>
      <div className={cn((isLoading || isError) && "opacity-50 pointer-events-none")}>
        {value === "custom"
          ? <div className="relative flex items-center">
              <Input
                disabled={disabled}
                autoFocus={true}
                onBlur={(event) => handleSelectChange(event.target.value || "")}
                onChange={handleInputChange}
                className="pr-8"
              />
              <div className="absolute right-1">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className={version ? "visible" : "invisible"}
                  onClick={handleClearClick}
                >
                  <X className="size-3" />
                </Button>
              </div>
            </div>
          : <FilterCombobox
              disabled={disabled}
              value={value || null}
              onChange={(val) => handleSelectChange(val ?? "")}
              placeholder=""
              className="w-full"
              searchable
              options={versionOptions}
            />
        }
      </div>
    </div>
  );
};

export default ServiceVersionSelect;
